import {v4 as uuid} from 'uuid';
import { AnessaListItem } from '../anessa-list-item.interface';


export interface ReportSectionInterface {
  subsections: ReportSection[];
  title: string;
  uuid: string;
  body: string;
}

export class ReportSection implements AnessaListItem {
  public subsections: ReportSection[];
  public title: string;
  uuid: string;
  public body: string;

  constructor(param: ReportSectionInterface = {
    subsections: [],
    title: '',
    uuid: null,
    body: '',
  }) {
    this.subsections = [];
    for (const sub of param.subsections) {
      this.subsections.push(new ReportSection(sub));
    }
    this.title = param.title;
    this.uuid = param.uuid ? param.uuid : uuid();
    this.body = param.body;
  }

  static deepCopy(section: ReportSection) {
    let copy;
    if (section) {
      copy = new ReportSection(section);
      return copy;
    }
    throw new Error('Report section copy failed');
  }
}


export interface ReportInterface {
  title: string;
  author: string;
  executiveSummary: string;
  includeDate: boolean;
  includeToc: boolean;
  includeListOfFigures: boolean;
  includeListOfTables: boolean;
  includeInputSummary: boolean;
  sections: ReportSection[];
}

export class Report {
  public title: string;
  public author: string;
  public executiveSummary: string;
  public includeDate: boolean;
  public includeToc: boolean;
  public includeListOfFigures: boolean;
  public includeListOfTables: boolean;
  public includeInputSummary: boolean;
  public sections: ReportSection[];

  constructor(param: ReportInterface = {
    title: '',
    author: '',
    executiveSummary: '',
    includeDate: false,
    includeToc: false,
    includeListOfFigures: false,
    includeListOfTables: false,
    includeInputSummary: false,
    sections: [],
  }) {
    this.title = param.title;
    this.author = param.author;
    this.executiveSummary = param.executiveSummary;
    this.includeDate = param.includeDate;
    this.includeToc = param.includeToc;
    this.includeListOfFigures = param.includeListOfFigures;
    this.includeListOfTables = param.includeListOfTables;
    this.includeInputSummary = param.includeInputSummary;
    this.sections = [];
    for (const section of param.sections) {
      this.sections.push(new ReportSection(section));
    }
  }

  static deepCopy(report: Report) {
    let copy;
    if (report) {
      copy = new Report(report);
      return copy;
    }
    throw new Error('Report copy failed');
  }
}

export interface ReportLocationInterface {
  section: number;
  subsection: number;
  subSubsection: number;
  reportIndex: number;
}

export class ReportLocation {
  public section: number;
  public subsection: number;
  public subSubsection: number;
  public reportIndex: number;

  constructor(param: ReportLocationInterface = {
    section: null,
    subsection: null,
    subSubsection: null,
    reportIndex: null,
  }) {
    this.section = param.section;
    this.subsection = param.subsection;
    this.subSubsection = param.subSubsection;
    this.reportIndex = param.reportIndex;
  }

  getSection(report: Report) {
    if (this.subSubsection >= 0 && this.subSubsection !== null &&
      this.subsection >= 0 && this.subsection !== null &&
      this.section >= 0 && this.section !== null) {
      return report.sections[this.section]
        .subsections[this.subsection]
        .subsections[this.subSubsection];
    }
    if (this.subsection >= 0 && this.subsection !== null &&
      this.section >= 0 && this.section !== null) {
      return report.sections[this.section]
        .subsections[this.subsection];
    }
    if (this.section >= 0 && this.section !== null) {
      return report.sections[this.section];
    }
    return null;
  }
}


export interface ReportConfig {
  title: string
  format: 'excel' | 'TODO'
  inputs: boolean,
  outputs: boolean,
  outputsYearly: boolean,
  author?: string,
  cashFlow?: boolean,
  pfd?: boolean
}